import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import VendorReviewQuestionnaire from "./VendorReviewQuestionnaire";
import { Box, Typography, Paper } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const SESSION_KEY_PREFIX = "vendor_review_submitted_";

const VendorReviewPublic = () => {
  const location = useLocation();
  const [reviewToken, setReviewToken] = useState(null);
  const [vendorName, setVendorName] = useState("");
  const [reviewCompleted, setReviewCompleted] = useState(false);
  const [wasAcknowledged, setWasAcknowledged] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tokenParam = queryParams.get("review_token");
    const vendorParam = queryParams.get("vendor_name");

    if (tokenParam) {
      // Check if this token was already submitted
      const sessionKey = SESSION_KEY_PREFIX + tokenParam;
      const alreadySubmitted = sessionStorage.getItem(sessionKey);
      if (alreadySubmitted) {
        setReviewCompleted(true);
        setWasAcknowledged(alreadySubmitted === "acknowledged");
      } else {
        setReviewToken(tokenParam);
      }
    }
    if (vendorParam) {
      setVendorName(vendorParam);
    }
  }, [location.search]);

  const handleSubmitSuccess = ({ acknowledged, submitted }) => {
    if (submitted) {
      const queryParams = new URLSearchParams(location.search);
      const tokenParam = queryParams.get("review_token");
      if (tokenParam) {
        const sessionKey = SESSION_KEY_PREFIX + tokenParam;
        sessionStorage.setItem(sessionKey, acknowledged ? "acknowledged" : "submitted");
      }
      setReviewCompleted(true);
      setWasAcknowledged(acknowledged);
    }
  };

  // After submission, on refresh show "Link Expired"
  if (reviewCompleted && !reviewToken) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#eeeeee",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 600, textAlign: "center" }}>
          <ErrorOutlineIcon sx={{ fontSize: 60, color: "#f44336", mb: 2 }} />
          <Typography
            variant="h5"
            sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 2, color: "#f44336" }}
          >
            Link Expired
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: "Lato", color: "#666" }}>
            This review link has already been used and is no longer valid. If you need to make
            further changes, please contact your administrator for a new review link.
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Right after submission, show Thank You or Review Submitted
  if (reviewCompleted && reviewToken) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#eeeeee",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 600, textAlign: "center" }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: "#4caf50", mb: 2 }} />
          <Typography
            variant="h5"
            sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 2, color: "#001b2b" }}
          >
            {wasAcknowledged ? "Thank You!" : "Review Submitted"}
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: "Lato", color: "#666" }}>
            {wasAcknowledged
              ? "Your acknowledgement has been recorded. You may now close this page."
              : "Your review has been submitted with flagged items. The assessment team will be notified. You may now close this page."}
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!reviewToken) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#eeeeee",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 3,
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 600, textAlign: "center" }}>
          <Typography
            variant="h5"
            sx={{ fontFamily: "Lato", fontWeight: "bold", mb: 2, color: "#f44336" }}
          >
            Invalid Access
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: "Lato", color: "#666" }}>
            The review link is invalid or has expired. Please contact your administrator for a new
            link.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <VendorReviewQuestionnaire
      reviewToken={reviewToken}
      vendorName={vendorName}
      onSubmitSuccess={handleSubmitSuccess}
    />
  );
};

export default VendorReviewPublic;
